<template>
  <div class="staff-timetable-report">
    <!-- Control Buttons -->
    <div class="no-print control-bar">
      <div class="d-flex align-items-center">
        <h4 class="mb-0">Staff TimeTable</h4>
        <b-badge v-show="!loading" variant="light-success" class="ml-2">
          {{ timetableItems.length }} Entries
        </b-badge>
      </div>
      <div class="action-buttons">
        <button class="btn-modern btn-print" @click="print">
          <feather-icon icon="PrinterIcon" size="18" class="mr-50" />
          <span>Print Report</span>
        </button>
        <button class="btn-modern btn-close" @click="close">
          <feather-icon icon="XIcon" size="18" class="mr-50" />
          <span>Close</span>
        </button>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="text-center py-5">
      <b-spinner variant="primary" style="width: 3.5rem; height: 3.5rem" />
      <p class="mt-3 font-weight-bold">Loading timetable details...</p>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="text-center py-5">
      <feather-icon icon="AlertCircleIcon" size="48" class="text-danger mb-3" />
      <h4 class="text-danger font-weight-bold">{{ error }}</h4>
      <b-button variant="primary" @click="loadReportData" class="mt-3"
        >Retry</b-button
      >
    </div>

    <!-- Report Content -->
    <div v-else class="report-page">
      <!-- Header -->
      <div class="report-header-modern mb-4">
        <div class="d-flex align-items-center">
          <div class="logo-wrapper mr-3" v-if="schoolLogo">
            <img :src="schoolLogo" alt="Logo" class="school-logo-img" />
          </div>
          <div class="header-text-block">
            <h2 class="school-name-full mb-0">
              {{ campusName
              }}<span v-if="campusBranch" class="text-muted">
                - {{ campusBranch }}</span
              >
            </h2>
            <div class="report-info-line mt-1">
              <span class="report-name-text">Staff TimeTable Report</span>
              <span class="date-range-text">
                <strong>Generated on {{ currentDateTime }}</strong>
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Summary Section -->
      <div class="summary-section mb-3">
        <div class="summary-card">
          <span class="label">Total Staff</span>
          <span class="value text-success">{{ uniqueStaff.length }}</span>
        </div>
        <div class="summary-card">
          <span class="label">Total Subjects</span>
          <span class="value text-info">{{ uniqueSubjects.length }}</span>
        </div>
        <div class="summary-card">
          <span class="label">Total Sessions</span>
          <span class="value text-warning">{{ timetableItems.length }}</span>
        </div>
      </div>

      <!-- Timetable by Class -->
      <div class="timetable-container" v-if="timetableItems.length > 0">
        <div
          v-for="classGroup in groupedByClass"
          :key="`${classGroup.classID}-${classGroup.sectionID}`"
          class="class-section mb-4"
        >
          <h4 class="class-header">
            {{ classGroup.className }} - {{ classGroup.sectionName }}
          </h4>

          <div class="table-container">
            <table class="ledger-style-table calendar-grid">
              <thead>
                <tr>
                  <th class="time-col-header">Period / Time</th>
                  <th
                    v-for="day in getActiveDaysForClass(
                      classGroup.classID,
                      classGroup.sectionID
                    )"
                    :key="day"
                    class="day-col-header"
                  >
                    {{ capitalizeDay(day) }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(slot, periodIndex) in getTimeSlotsForClass(
                    classGroup.classID,
                    classGroup.sectionID
                  )"
                  :key="slot.key"
                  class="period-row"
                >
                  <td class="time-col">
                    <span class="period-no">P{{ periodIndex + 1 }}</span>
                    <span class="period-time">
                      {{ formatTime(slot.startTime) }} -
                      {{ formatTime(slot.endTime) }}
                    </span>
                  </td>
                  <td
                    v-for="day in getActiveDaysForClass(
                      classGroup.classID,
                      classGroup.sectionID
                    )"
                    :key="`${day}-${slot.key}`"
                    class="session-cell"
                  >
                    <div
                      v-if="
                        getCellItems(
                          classGroup.classID,
                          classGroup.sectionID,
                          day,
                          slot.key
                        ).length
                      "
                      class="session-block"
                    >
                      <div
                        v-for="(item, index) in getCellItems(
                          classGroup.classID,
                          classGroup.sectionID,
                          day,
                          slot.key
                        )"
                        :key="index"
                      >
                        <div class="session-subject">
                          {{ item.meta_subjectName || "-" }}
                        </div>
                        <div class="session-teacher">
                          {{ item.meta_teacherName || "-" }}
                        </div>
                      </div>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- If No Data -->
      <div v-else class="text-center py-5 text-muted">
        <feather-icon icon="AlertCircleIcon" size="48" class="mb-3" />
        <p>No timetable data available for the selected filters.</p>
      </div>
    </div>
  </div>
</template>

<script>
import { BButton, BSpinner, BBadge } from "bootstrap-vue";
import axios from "axios";

export default {
  name: "StaffTimeTablePrint",
  components: {
    BButton,
    BSpinner,
    BBadge,
  },
  data() {
    return {
      loading: false,
      error: null,
      timetableItems: [],
      schoolLogo: null,
      campusName: "",
      campusBranch: "",
      campusAddress: "",
      db: "",
      cID: "",
      clsID: 0,
      section: 0,
      tID: 0,
      subID: 0,
      reportDomain: (this.$store.state.report || "").replace(/_/g, ""),
      orderedDays: [
        "monday",
        "tuesday",
        "wednesday",
        "thursday",
        "friday",
        "saturday",
        "sunday",
      ],
    };
  },
  computed: {
    currentDateTime() {
      const now = new Date();
      return now.toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      });
    },
    uniqueClasses() {
      return [...new Set(this.timetableItems.map((item) => item.clsID))].length;
    },
    uniqueStaff() {
      return [...new Set(this.timetableItems.map((item) => item.tID))];
    },
    uniqueSubjects() {
      return [...new Set(this.timetableItems.map((item) => item.subID))];
    },
    groupedByClass() {
      const grouped = {};
      this.timetableItems.forEach((item) => {
        const key = `${item.clsID}-${item.section}`;
        if (!grouped[key]) {
          grouped[key] = {
            classID: item.clsID,
            sectionID: item.section,
            className: item.meta_className,
            sectionName: item.meta_sectionName,
            days: [],
          };
        }
        // Only add day if it's not null and not already in the list
        if (item.day && !grouped[key].days.includes(item.day)) {
          grouped[key].days.push(item.day);
        }
      });
      return Object.values(grouped);
    },
  },
  created() {
    this.initReport();
  },
  methods: {
    initReport() {
      const query = this.$route.query;
      this.db = query.db;
      this.cID = query.cID || query.cId;
      this.clsID = query.clsID || 0;
      this.section = query.section || 0;
      this.tID = query.tID || 0;
      this.subID = query.subID || 0;

      this.campusName =
        (this.$store.state.currentBranch &&
          this.$store.state.currentBranch.branch) ||
        "My Skool";
      this.schoolLogo =
        (this.$store.state.currentBranch &&
          this.$store.state.currentBranch.logo) ||
        null;

      if (!this.db) {
        this.error = "Invalid parameters. Please close and try again.";
        return;
      }

      this.loadCampusInfo();
      this.loadReportData();
    },
    async loadCampusInfo() {
      try {
        const url = `https://apiv26.myskool.app/api/campuses`;
        const response = await axios.get(url, {
          params: {
            db: this.db,
            cId: this.cID,
          },
          headers: {
            Authorization: `Bearer ${this.$store.state.userData.token}`,
            accept: "application/json",
          },
        });

        if (
          response.data &&
          response.data.status === "success" &&
          response.data.data &&
          response.data.data.length > 0
        ) {
          // Find the campus that matches cID, fallback to first if cID is not explicitly found or is 0
          const match = response.data.data.find((c) => c.id == this.cID);
          const info = match || response.data.data[0];

          this.campusName = info.name;
          this.campusBranch = this.cID == 0 ? "All Campuses" : info.branch;
          this.schoolLogo = info.logo;
          this.campusAddress = info.address;
        }
      } catch (e) {
        console.error("Failed to load campus info:", e);
      }
    },
    async loadReportData() {
      try {
        this.loading = true;
        this.error = null;

        let baseUrl = this.$store.state.domain;
        if (baseUrl.endsWith("/api/")) {
          // keep it as is
        } else if (!baseUrl.endsWith("/")) {
          baseUrl += "/";
        }

        const url = `${baseUrl}TimeTable/StaffTimeTable`;

        const response = await axios.get(url, {
          params: {
            db: this.db,
            cID: this.cID,
            clsID: this.clsID,
            section: this.section,
            tID: this.tID,
            subID: this.subID,
          },
          headers: {
            Authorization: `Bearer ${this.$store.state.userData.token}`,
            accept: "application/json",
          },
        });

        if (response.data && response.data.status === "success") {
          this.timetableItems = response.data.data || [];
        } else {
          this.error =
            response.data?.message || "Failed to load timetable data.";
        }
      } catch (error) {
        console.error("Failed to load staff timetable:", error);
        this.error =
          "Network error or server unavailable. Please ensure the API is running.";
      } finally {
        this.loading = false;
      }
    },
    getClassDayTimetable(classID, sectionID, day) {
      return this.timetableItems
        .filter(
          (item) =>
            item.clsID === classID &&
            item.section === sectionID &&
            item.day &&
            item.day.toLowerCase() === day.toLowerCase()
        )
        .sort((a, b) => {
          const timeA = this.formatTime(a.startTime);
          const timeB = this.formatTime(b.startTime);

          const dateA = new Date(`2000-01-01 ${timeA}`);
          const dateB = new Date(`2000-01-01 ${timeB}`);

          return dateA - dateB;
        });
    },
    getTimeSlotKey(item) {
      return this.formatTime(item.startTime);
    },
    parseTimeToMinutes(time) {
      const formatted = this.formatTime(time);
      const date = new Date(`2000-01-01 ${formatted}`);
      return Number.isNaN(date.getTime())
        ? 0
        : date.getHours() * 60 + date.getMinutes();
    },
    getTimeSlotsForClass(classID, sectionID) {
      const slotMap = {};
      this.timetableItems
        .filter((item) => item.clsID === classID && item.section === sectionID)
        .forEach((item) => {
          const key = this.getTimeSlotKey(item);
          if (!slotMap[key]) {
            slotMap[key] = {
              key,
              startTime: item.startTime,
              endTime: item.endTime,
              sortValue: this.parseTimeToMinutes(item.startTime),
            };
          }
        });
      return Object.values(slotMap).sort((a, b) => a.sortValue - b.sortValue);
    },
    getActiveDaysForClass(classID, sectionID) {
      return this.orderedDays.filter(
        (day) => this.getClassDayTimetable(classID, sectionID, day).length > 0
      );
    },
    getCellItems(classID, sectionID, day, slotKey) {
      return this.timetableItems.filter(
        (item) =>
          item.clsID === classID &&
          item.section === sectionID &&
          item.day &&
          item.day.toLowerCase() === day.toLowerCase() &&
          this.getTimeSlotKey(item) === slotKey
      );
    },
    normalizeMeridiem(value) {
      if (!value) return null;
      const token = String(value).trim().toUpperCase();
      if (token === "A" || token === "AM") return "AM";
      if (token === "P" || token === "PM") return "PM";
      return null;
    },
    guessMeridiem(hour) {
      if (hour === 12) return "PM";
      if (hour >= 1 && hour < 7) return "PM";
      if (hour >= 7 && hour < 12) return "AM";
      return "PM";
    },
    parseTimeValue(time) {
      if (!time) return null;
      let raw = String(time).trim().replace(/\s+/g, " ");
      if (!raw) return null;

      let match = raw.match(/^(\d{1,2}):(\d{2})(?::\d{2})?\s*(AM|PM|A|P)?$/i);
      if (match) {
        let hour = parseInt(match[1], 10);
        const minute = parseInt(match[2], 10);
        let meridiem = this.normalizeMeridiem(match[3]);

        if (hour >= 13 && hour <= 23) {
          meridiem = "PM";
          hour -= 12;
        } else if (hour === 0) {
          hour = 12;
          meridiem = "AM";
        }

        if (!meridiem) {
          meridiem = this.guessMeridiem(hour);
        }

        return { hour, minute, meridiem };
      }

      match = raw.match(/^(\d{1,2})\s*(AM|PM|A|P)?$/i);
      if (match) {
        let hour = parseInt(match[1], 10);
        let meridiem = this.normalizeMeridiem(match[2]);

        if (hour >= 13 && hour <= 23) {
          meridiem = "PM";
          hour -= 12;
        } else if (hour === 0) {
          hour = 12;
          meridiem = "AM";
        }

        if (!meridiem) {
          meridiem = this.guessMeridiem(hour);
        }

        return { hour, minute: 0, meridiem };
      }

      return null;
    },
    formatTime(time) {
      const parsed = this.parseTimeValue(time);
      if (!parsed) return String(time || "").trim();

      const minute = String(parsed.minute).padStart(2, "0");
      return `${parsed.hour}:${minute} ${parsed.meridiem}`;
    },
    capitalizeDay(day) {
      if (!day || typeof day !== "string") return "Unknown";
      return day.charAt(0).toUpperCase() + day.slice(1);
    },
    print() {
      window.print();
    },
    close() {
      window.close();
    },
  },
};
</script>

<style lang="scss" scoped>
.staff-timetable-report {
  background: #f0f2f5;
  min-height: 100vh;
  padding: 2.5rem;
  font-family: "Outfit", "Inter", sans-serif;
  color: #2d3436;
}

.control-bar {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  padding: 1.25rem 2.5rem;
  margin-bottom: 2rem;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 1rem;
  z-index: 1000;
  border: 1px solid rgba(255, 255, 255, 0.3);

  h4 {
    color: #444;
    font-weight: 700;
    font-size: 1.25rem;
    letter-spacing: -0.5px;
  }
}

.action-buttons {
  display: flex;
  gap: 12px;
}

.btn-modern {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px 24px;
  border-radius: 10px;
  font-weight: 700;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  border: none;
  outline: none;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);

  span {
    margin-left: 4px;
  }

  &:active {
    transform: scale(0.96);
  }
}

.btn-print {
  background: linear-gradient(135deg, #6c5ce7 0%, #a29bfe 100%);
  color: white;

  &:hover {
    background: linear-gradient(135deg, #5b4cc4 0%, #8c82eb 100%);
    box-shadow: 0 6px 20px rgba(108, 92, 231, 0.35);
    transform: translateY(-1px);
  }
}

.btn-close {
  background: white;
  color: #636e72;
  border: 1px solid #dfe6e9;

  &:hover {
    background: #fdfdfd;
    color: #e74c3c;
    border-color: #fab1a0;
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.05);
    transform: translateY(-1px);
  }
}

.report-page {
  background: #ffffff;
  padding: 3rem 2.5rem;
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.12);
  width: 98%;
  max-width: 1200px;
  margin: 30px auto;
  transition: all 0.3s ease;
  box-sizing: border-box;
}

.report-header-modern {
  border-bottom: 2px solid #333;
  padding-bottom: 20px;

  .school-logo-img {
    height: 130px;
    width: auto;
    object-fit: contain;
  }

  .school-name-full {
    font-size: 2rem;
    font-weight: 800;
    color: #000;
    text-transform: uppercase;
    letter-spacing: -0.5px;
  }

  .report-info-line {
    font-size: 1.1rem;
    color: #444;
    display: flex;
    align-items: center;

    .report-name-text {
      font-weight: 700;
      border-right: 2px solid #ddd;
      padding-right: 15px;
      margin-right: 15px;
    }

    .date-range-text {
      color: #555;
    }
  }
}

.summary-section {
  display: flex;
  justify-content: space-around;
  flex-wrap: wrap;
  gap: 1.5rem;
  padding: 1.5rem;
  background: #f8f9fa;
  border-radius: 12px;
  border: 1px solid #eee;

  .summary-card {
    display: flex;
    flex-direction: column;
    align-items: center;

    .label {
      font-size: 0.8rem;
      font-weight: 700;
      color: #636e72;
      text-transform: uppercase;
      letter-spacing: 1px;
      margin-bottom: 4px;
    }

    .value {
      font-size: 1.4rem;
      font-weight: 800;
    }
  }
}

.timetable-container {
  margin-top: 0.5rem;
}

.class-section {
  page-break-inside: avoid;
}

.class-header {
  font-size: 1.1rem;
  font-weight: 800;
  color: #2c3e50;
  margin-bottom: 0.75rem;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid #6c5ce7;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.table-container {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;

  &::-webkit-scrollbar {
    height: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background: #c8cdd3;
    border-radius: 4px;
  }
}

.ledger-style-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 0;
  border: 1px solid #dee2e6;
  background: white;

  th,
  td {
    border: 1px solid #dee2e6;
    box-sizing: border-box;
  }

  thead {
    background: #2c3e50 !important;

    th {
      color: white !important;
      font-weight: 800;
      text-transform: uppercase;
      font-size: 0.85rem;
      letter-spacing: 0.5px;
      padding: 10px 8px;
      text-align: center;
    }
  }

  tbody tr:nth-child(even) {
    background-color: #f8f9fa;
  }

  tbody tr:hover {
    background: #f1f4ff;
  }
}

.calendar-grid {
  min-width: 700px;
  table-layout: fixed;
}

.time-col-header {
  width: 100px;
  min-width: 100px;
}

.time-col {
  width: 100px;
  min-width: 100px;
  padding: 8px 6px;
  text-align: center;
  vertical-align: middle;
  background: #f8f9fa;
}

.period-no {
  display: block;
  font-size: 0.7rem;
  font-weight: 800;
  color: #6c5ce7;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  line-height: 1.2;
}

.period-time {
  display: block;
  font-size: 0.68rem;
  font-weight: 600;
  color: #636e72;
  line-height: 1.3;
  margin-top: 2px;
  white-space: nowrap;
}

.session-cell {
  padding: 8px 6px;
  text-align: center;
  vertical-align: middle;
}

.session-block {
  line-height: 1.3;
}

.session-subject {
  font-size: 0.78rem;
  font-weight: 800;
  color: #2d3436;
  text-transform: uppercase;
  letter-spacing: 0.2px;
  word-break: break-word;
}

.session-teacher {
  font-size: 0.72rem;
  font-weight: 500;
  color: #636e72;
  margin-top: 2px;
  word-break: break-word;
}

.text-center {
  text-align: center;
}
.font-weight-bold {
  font-weight: 700;
}

@page {
  size: A4 landscape;
  margin: 0;
}

@media print {
  .no-print {
    display: none !important;
  }

  body {
    background: white !important;
    margin: 0 !important;
    padding: 0 !important;
    color: #000 !important;
  }

  .staff-timetable-report {
    background: white !important;
    padding: 0 !important;
  }

  .report-page {
    box-shadow: none !important;
    border: none !important;
    padding: 1.2cm 0.8cm !important;
    max-width: 100% !important;
    width: 100% !important;
    margin: 0 !important;
  }

  .table-container {
    overflow: visible !important;
  }

  .ledger-style-table {
    font-size: 8pt !important;
    width: 100% !important;

    th,
    td {
      padding: 5px 6px !important;
      border: 1px solid #000 !important;
      color: #000 !important;
      background: #fff !important;
      box-shadow: none !important;
      outline: none !important;
    }

    thead th {
      background: #f1f2f6 !important;
      color: #000 !important;
      font-weight: 800 !important;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }

    tbody tr:nth-child(even) td {
      background: #fff !important;
    }

    tbody tr:hover,
    tbody tr:hover td {
      background: #fff !important;
    }
  }

  .session-cell:empty {
    background: #fff !important;
  }

  svg,
  .feather,
  .feather-icon {
    display: none !important;
  }

  .report-header-modern {
    margin-bottom: 15px !important;
    padding-bottom: 12px !important;
    border-bottom: 4px solid #000 !important;

    .school-logo-img {
      height: 80px !important;
      max-width: 220px !important;
      object-fit: contain !important;
    }

    .school-name-full {
      font-size: 1.4rem !important;
      color: #000 !important;
    }

    .report-info-line {
      font-size: 0.9rem !important;
      color: #000 !important;

      .report-name-text {
        border-right-color: #000 !important;
      }
    }
  }

  .summary-section {
    margin: 5px 0 10px !important;
    padding: 8px !important;
    border: 1px solid #000 !important;
    display: flex !important;
    flex-direction: row !important;
    justify-content: space-around !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;

    .summary-card {
      flex-direction: row !important;
      gap: 8px !important;
      align-items: center !important;

      .label {
        font-size: 0.7rem !important;
        margin-bottom: 0 !important;
        color: #000 !important;
      }

      .value {
        font-size: 1rem !important;
        color: #000 !important;
      }
    }
  }

  .class-header {
    font-size: 10pt !important;
    margin-bottom: 6px !important;
    padding-bottom: 4px !important;
    border-bottom-color: #000 !important;
    color: #000 !important;
  }

  .period-no {
    color: #000 !important;
    font-size: 7pt !important;
  }

  .period-time {
    font-size: 6.5pt !important;
    color: #333 !important;
  }

  .session-subject {
    font-size: 7.5pt !important;
    color: #000 !important;
  }

  .session-teacher {
    font-size: 7pt !important;
    color: #333 !important;
  }

  .class-section {
    page-break-inside: avoid;
    margin-bottom: 12px !important;
  }
}
</style>
